import type { ProductType } from "@component/assets/AbstractLeadForm/types";
import type {
	AbstractListingCalculatorProps,
	AbstractListingCalculatorState,
} from "@component/assets/AbstractListingCalculator/AbstractListingCalculator";
import { AbstractListingCalculator } from "@component/assets/AbstractListingCalculator/AbstractListingCalculator";
import type { ResultListResponse } from "@component/assets/AbstractListingCalculator/types";
import { LandingCover } from "@landing/components/LandingCover";
import type { EventComponentType } from "@utils/google-analytics-4.type";
import { ResponsiveHelper } from "@utils/responsive-helper";
import autobind from "autobind-decorator";
import dayjs from "dayjs";
import React from "react";
import type { ReactNode } from "react";
import type { LoanGoal } from "../LeadForm/types";
import { PersonalLoanResultList } from "./PersonalLoanResultList";
import { PersonalLoanSearchForm } from "./PersonalLoanSearchForm";
import { BASIC_SEARCH_FIELDS } from "./data";
import { calculate, exportXls, getDefaultParams, responseToResult, searchParamsToRequest } from "./domain";
import type {
	PersonalLoanCalculatorVariant,
	PersonalLoanRequest,
	PersonalLoanResponseItem,
	PersonalLoanSearchParams,
	PersonalLoanSort,
} from "./types";
import "./style.scss";

interface Props extends AbstractListingCalculatorProps {
	title: ReactNode;
	subTitle: ReactNode;
	button?: string;
	loanGoal?: LoanGoal;
	highlightedBank?: string;
	hideLoanGoal?: boolean;
	employmentMonths?: number;
	income?: number;
	loanAmount?: number;
	variant?: PersonalLoanCalculatorVariant;
}

interface State
	extends AbstractListingCalculatorState<
		PersonalLoanSearchParams,
		PersonalLoanRequest,
		PersonalLoanResponseItem,
		PersonalLoanSort
	> {
	mobileView?: boolean;
}

export class PersonalLoanCalculator extends AbstractListingCalculator<
	PersonalLoanSearchParams,
	PersonalLoanRequest,
	PersonalLoanResponseItem,
	PersonalLoanSort,
	Props,
	State
> {
	// EventParams
	protected readonly productTypes: string = "PERSONAL_LOAN";
	protected readonly componentType: EventComponentType = "listing-calculator";
	private responsiveHelper;

	public componentDidMount(): void {
		super.componentDidMount();

		this.responsiveHelper.startObserving();
	}

	public componentWillUnmount(): void {
		super.componentWillUnmount();

		this.responsiveHelper.stopObserving();
	}

	protected getInitialState(): State {
		this.responsiveHelper = new ResponsiveHelper("lg", (isOver) => {
			this.setState({
				mobileView: !isOver,
			});
		});

		return {
			...super.getInitialState(),
			mobileView: !this.responsiveHelper.getActualStatus(),
		};
	}

	protected getDefaultValues(): PersonalLoanSearchParams {
		const { variant } = this.props;
		const params = getDefaultParams(variant);
		const {
			loanGoal = params.loanGoal,
			employmentMonths = params.employmentMonths,
			income = params.income,
			loanAmount = params.loanAmount,
		} = this.props;

		return {
			...params,
			calculationDate: dayjs().format("YYYY-MM-DD"),
			loanGoal,
			employmentMonths,
			income: income < 0 ? undefined : income,
			loanAmount: loanAmount < 0 ? undefined : loanAmount,
			onePerBank: !this.props.highlightedBank,
		};
	}

	public renderContent(): ReactNode {
		const {
			title,
			subTitle,
			loanGoal,
			hideLoanGoal,
			marketingFilter = this.marketingFilter,
			fakeHeader,
			variant,
		} = this.props;
		const { searchParams, mobileView } = this.state;

		return (
			<>
				<LandingCover
					title={title}
					subTitle={subTitle}
					fakeHeader={fakeHeader}
					showSubTitleOnMobileDevice
					className="landing-cover"
				>
					<PersonalLoanSearchForm
						loanGoal={loanGoal}
						variant={variant}
						hideLoanGoal={hideLoanGoal}
						{...this.getSearchFormProps()}
					/>
				</LandingCover>
				{searchParams && (
					<PersonalLoanResultList
						{...this.getResultListProps()}
						maxHighlight={marketingFilter === "ct-only" ? 10 : 4}
						mobileView={mobileView}
						hideLeadForm={marketingFilter === "ct-only"}
					/>
				)}
			</>
		);
	}

	protected getRequestFromSearchParams(searchParams: PersonalLoanSearchParams): PersonalLoanRequest {
		const { variant } = this.props;

		return searchParamsToRequest(searchParams, searchParams.sort, variant);
	}

	protected async getResult(request: PersonalLoanRequest): Promise<any> {
		return calculate(request);
	}

	protected getResultList(
		request: PersonalLoanRequest,
		searchParams: PersonalLoanSearchParams,
		response: any,
	): ResultListResponse<PersonalLoanResponseItem> {
		const { marketingFilter = this.marketingFilter } = this.props;

		return responseToResult(request, response, searchParams.onePerBank, marketingFilter);
	}

	@autobind
	protected async onExport(searchParams: PersonalLoanSearchParams): Promise<string> {
		return exportXls(searchParams);
	}

	protected getClassName(): string {
		return `${super.getClassName()} personal-loans`;
	}

	protected getProductType(): ProductType {
		return "PERSONAL_LOAN";
	}

	protected getBasicSearchFields(): string[] {
		return BASIC_SEARCH_FIELDS;
	}

	protected getComponentName(): string {
		return "PersonalLoanCalculator";
	}
}
